import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from './SVGIcons';

const ImageSlider = ({ images, alt = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Фильтруем только непустые изображения
  const validImages = (images || []).filter(img => img && img.trim() !== '');

  // Сброс индекса при изменении списка изображений
  useEffect(() => {
    setCurrentIndex(0);
  }, [images]);

  const goToPrev = useCallback((e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setCurrentIndex(prev => (prev === 0 ? validImages.length - 1 : prev - 1));
  }, [validImages.length]);

  const goToNext = useCallback((e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setCurrentIndex(prev => (prev === validImages.length - 1 ? 0 : prev + 1));
  }, [validImages.length]);

  const openLightbox = () => setLightboxOpen(true);
  const closeLightbox = () => setLightboxOpen(false);

  // Если изображений нет — ничего не показываем
  if (validImages.length === 0) return null;

  // Если изображение одно — показываем без стрелок, но с возможностью увеличения
  if (validImages.length === 1) {
    return (
      <>
        <div
          className='flex-none rounded-[14px] bg-[#F6F7F8] flex items-center justify-center w-full cursor-pointer overflow-hidden'
          onClick={openLightbox}
        >
          <img
            src={validImages[0]}
            alt={alt}
            width={244}
            height={356}
            className='h-auto object-contain hover:scale-105 transition-transform duration-300'
          />
        </div>

        {/* Lightbox */}
        {lightboxOpen && (
          <Lightbox
            images={validImages}
            currentIndex={currentIndex}
            onClose={closeLightbox}
            onPrev={goToPrev}
            onNext={goToNext}
            alt={alt}
          />
        )}
      </>
    );
  }

  // Несколько изображений — слайдер со стрелками
  return (
    <>
      <div className='relative flex-none rounded-[14px] bg-[#F6F7F8] flex items-center justify-center w-full overflow-hidden group'>
        {/* Основное изображение */}
        <div
          className='w-full cursor-pointer'
          onClick={openLightbox}
        >
          <img
            src={validImages[currentIndex]}
            alt={`${alt} ${currentIndex + 1}`}
            width={244}
            height={356}
            className='h-auto object-contain w-full hover:scale-105 transition-transform duration-300'
          />
        </div>

        {/* Стрелка влево — на мобильных всегда видна, на десктопе по hover */}
        <button
          onClick={goToPrev}
          className='absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-white shadow-md md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 z-10'
          aria-label='Предыдущее изображение'
        >
          <ChevronLeftIcon width={8} height={14} stroke='#001553' />
        </button>

        {/* Стрелка вправо — на мобильных всегда видна, на десктопе по hover */}
        <button
          onClick={goToNext}
          className='absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-white shadow-md md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 z-10'
          aria-label='Следующее изображение'
        >
          <ChevronRightIcon width={8} height={14} stroke='#001553' />
        </button>

        {/* Индикаторы (точки) */}
        <div className='absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2'>
          {validImages.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                idx === currentIndex
                  ? 'bg-[#001553] w-3'
                  : 'bg-[#001553]/40 hover:bg-[#001553]/60'
              }`}
              aria-label={`Изображение ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={validImages}
          currentIndex={currentIndex}
          onClose={closeLightbox}
          onPrev={goToPrev}
          onNext={goToNext}
          alt={alt}
        />
      )}
    </>
  );
};

// Компонент Lightbox (увеличенное изображение)
const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext, alt }) => {
  // Закрытие по Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  // Блокировка скролла страницы
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      className='fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4'
      onClick={onClose}
    >
      {/* Кнопка закрытия */}
      <button
        onClick={onClose}
        className='absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-colors z-10'
        aria-label='Закрыть'
      >
        <CloseIcon width={24} height={24} fill='white' />
      </button>

      {/* Контент */}
      <div
        className='relative w-full h-full flex items-center justify-center'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Стрелка влево (только если > 1 изображения) — внутри контейнера, не вылезает за края */}
        {images.length > 1 && (
          <button
            onClick={onPrev}
            className='absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-colors z-20'
            aria-label='Предыдущее'
          >
            <ChevronLeftIcon width={10} height={18} stroke='white' />
          </button>
        )}

        {/* Изображение */}
        <img
          src={images[currentIndex]}
          alt={`${alt} ${currentIndex + 1}`}
          className='max-w-[90vw] max-h-[85vh] object-contain rounded-lg'
        />

        {/* Стрелка вправо (только если > 1 изображения) — внутри контейнера, не вылезает за края */}
        {images.length > 1 && (
          <button
            onClick={onNext}
            className='absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-colors z-20'
            aria-label='Следующее'
          >
            <ChevronRightIcon width={10} height={18} stroke='white' />
          </button>
        )}
      </div>

      {/* Счетчик */}
      {images.length > 1 && (
        <div className='absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full'>
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

export default ImageSlider;
